import {
    useCallback,
    useEffect,
    useRef,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";
import {
    useSearchParams,
} from "react-router-dom";

import type {
    Filters,
} from "@mrflark/spl_service_model";
import {
    splClient,
} from "../api/client.ts";
import {
    BROWSING_MODES,
    UNTAGGED_TAG_QUERY,
} from "../constants.ts";
import type {
    BrowsingMode,
} from "../constants.ts";
import {
    useViewerStore,
} from "../stores/viewerStore.ts";
import {
    fetchOrderedMedia,
} from "./useViewerNavigation.ts";
import {
    extractErrorMessage,
} from "./utils.ts";

type UseViewerFiltersReturn = {
    handleApplyFilters: () => void;
    handleBrowsingModeChange: (mode: BrowsingMode) => Promise<void>;
    handleSearch: () => Promise<void>;
    handleFetchExactCount: () => Promise<void>;
    handleTagStatsClick: (tagOrQuery: string) => Promise<void>;
    handleClearSearchAndBrowse: () => void;
};

export const useViewerFilters = (
    loadNextItem: () => Promise<void>,
): UseViewerFiltersReturn => {
    const [searchParams, setSearchParams] = useSearchParams();
    const hasInitialized = useRef(false);

    const {
        currentItem,
        browsingMode,
        filters,
        searchQuery,
        setBrowsingMode,
        setCurrentItem,
        setIsLoading,
        setError,
        setFilters,
        clearFilters: _clearFilters,
        setTotalFound,
        setExactCount,
        setIsLoadingExactCount,
        setOrderedItems,
        setPrefetchedItem,
        addRecentlyViewed,
        addToHistory,
        clearSearch,
        resetOrderedState,
        setIsSearching,
        setSearchResults,
        setActiveLeftTab,
    } = useViewerStore();

    // --- initialization from URL ---
    useEffect(() => {
        const tagQuery = searchParams.get("tag_query") || "";
        const minViews = searchParams.get("min_views");
        const maxViews = searchParams.get("max_views");
        const minVotes = searchParams.get("min_votes");
        const maxVotes = searchParams.get("max_votes");
        const searchText = searchParams.get("search_text") || "";
        const mediaType = searchParams.get("media_type") as
            | "all"
            | "image"
            | "video"
            | null;
        const mode = searchParams.get("mode") as BrowsingMode | null;
        const itemId = searchParams.get("id");
        const hasWatermark = searchParams.get("has_watermark");
        const minAiConfidence = searchParams.get("min_ai_confidence");

        const effectiveMode = (mode && BROWSING_MODES.includes(mode))
            ? mode
            : browsingMode;

        if (mode && BROWSING_MODES.includes(mode)) {
            setBrowsingMode(mode);
        }

        const urlFilters: Filters = {
            tag_query: tagQuery,
            min_views: minViews ? parseInt(minViews) : undefined,
            max_views: maxViews ? parseInt(maxViews) : undefined,
            min_votes: minVotes ? parseInt(minVotes) : undefined,
            max_votes: maxVotes ? parseInt(maxVotes) : undefined,
            search_text: searchText,
            media_type: mediaType || "all",
            has_watermark: hasWatermark === "true" ? true : undefined,
            min_ai_confidence: minAiConfidence
                ? parseFloat(minAiConfidence)
                : undefined,
        };
        setFilters(urlFilters);

        const initialize = async (): Promise<void> => {
            setIsLoading(true);
            setError(null);
            try {
                if (itemId) {
                    const item = await splClient.getMediaItem(itemId);
                    setCurrentItem(item);
                    addRecentlyViewed(item.id, item);
                    addToHistory(item.id);
                } else if (effectiveMode === "random") {
                    const response = await splClient.getNextImage({
                        id: undefined,
                        filters: urlFilters,
                        exclude_ids: [],
                    });
                    setCurrentItem(response);
                    setTotalFound(response.total_found);
                    addRecentlyViewed(response.id, response);
                    addToHistory(response.id);
                } else {
                    const response = await fetchOrderedMedia(
                        effectiveMode,
                        urlFilters,
                        0,
                        50,
                    );
                    if (response.items.length === 0) {
                        setError("No media found");
                        return;
                    }
                    setOrderedItems(
                        response.items,
                        response.total_count,
                        response.has_more,
                    );
                    setTotalFound(response.total_count);
                    const firstItem = response.items[0];
                    setCurrentItem(firstItem);
                    addRecentlyViewed(firstItem.id, firstItem);
                    addToHistory(firstItem.id);
                }
            } catch (err) {
                setError(extractErrorMessage(err, "Failed to load media"));
            } finally {
                setIsLoading(false);
                hasInitialized.current = true;
            }
        };

        initialize();
    }, []);

    // --- URL param sync ---
    const updateUrlParams = useCallback(() => {
        const params = new URLSearchParams();
        if (browsingMode !== "random") params.set("mode", browsingMode);
        if (filters.tag_query) params.set("tag_query", filters.tag_query);
        if (filters.min_views !== undefined) {
            params.set("min_views", String(filters.min_views));
        }
        if (filters.max_views !== undefined) {
            params.set("max_views", String(filters.max_views));
        }
        if (filters.min_votes !== undefined) {
            params.set("min_votes", String(filters.min_votes));
        }
        if (filters.max_votes !== undefined) {
            params.set("max_votes", String(filters.max_votes));
        }
        if (filters.search_text) params.set("search_text", filters.search_text);
        if (filters.media_type && filters.media_type !== "all") {
            params.set("media_type", filters.media_type);
        }
        if (filters.has_watermark) params.set("has_watermark", "true");
        if (filters.min_ai_confidence !== undefined) {
            params.set("min_ai_confidence", String(filters.min_ai_confidence));
        }
        if (currentItem?.id) params.set("id", currentItem.id);
        setSearchParams(params, {
            replace: true,
        });
    }, [browsingMode, filters, currentItem?.id, setSearchParams]);

    useEffect(() => {
        if (!hasInitialized.current) return;
        updateUrlParams();
    }, [updateUrlParams]);

    // --- handlers ---
    const handleApplyFilters = useCallback(() => {
        clearSearch();
        resetOrderedState();
        loadNextItem();
    }, [clearSearch, resetOrderedState, loadNextItem]);

    const handleBrowsingModeChange = useCallback(async (mode: BrowsingMode) => {
        if (mode === browsingMode) return;

        setBrowsingMode(mode);
        clearSearch();
        setPrefetchedItem(null);
        setIsLoading(true);
        setError(null);

        const effectiveFilters = mode === "untagged"
            ? {
                ...filters,
                tag_query: UNTAGGED_TAG_QUERY,
            }
            : filters;

        if (mode === "untagged") {
            setFilters({
                tag_query: UNTAGGED_TAG_QUERY,
            });
        }

        try {
            if (mode === "random") {
                const filtersToSend = effectiveFilters
                    ? {
                        ...effectiveFilters,
                    }
                    : {};
                if (filtersToSend.media_type === "all") {
                    delete filtersToSend.media_type;
                }
                const response = await splClient.getNextImage({
                    id: undefined,
                    filters: filtersToSend,
                    exclude_ids: [],
                });
                setCurrentItem(response);
                setTotalFound(response.total_found);
                addRecentlyViewed(response.id, response);
                addToHistory(response.id);
            } else {
                const response = await fetchOrderedMedia(
                    mode,
                    effectiveFilters,
                    0,
                    50,
                );
                if (response.items.length === 0) {
                    setError("No media found");
                    return;
                }
                setOrderedItems(
                    response.items,
                    response.total_count,
                    response.has_more,
                );
                setTotalFound(response.total_count);
                const firstItem = response.items[0];
                setCurrentItem(firstItem);
                addRecentlyViewed(firstItem.id, firstItem);
                addToHistory(firstItem.id);
            }
        } catch (err) {
            const message = extractErrorMessage(err, "Failed to load media");
            setError(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    }, [
        browsingMode,
        filters,
        setBrowsingMode,
        clearSearch,
        setPrefetchedItem,
        setIsLoading,
        setError,
        setCurrentItem,
        setTotalFound,
        setOrderedItems,
        addRecentlyViewed,
        addToHistory,
        setFilters,
    ]);

    const handleSearch = useCallback(async () => {
        if (!searchQuery.trim()) return;

        setIsSearching(true);
        setError(null);

        try {
            const response = await splClient.search({
                query: searchQuery,
                mode: "unified",
                filters: {
                    tag_query: filters.tag_query || undefined,
                    media_type: filters.media_type,
                    min_views: filters.min_views,
                    max_views: filters.max_views,
                    min_votes: filters.min_votes,
                    max_votes: filters.max_votes,
                    has_watermark: filters.has_watermark,
                    min_ai_confidence: filters.min_ai_confidence,
                },
                limit: 50,
                offset: 0,
            });

            if (response.items.length === 0) {
                setError("No results found");
                setSearchResults([], 0, false, response.query_interpretation);
                return;
            }

            setSearchResults(
                response.items,
                response.total_count,
                response.has_more,
                response.query_interpretation,
            );

            const firstItem = response.items[0];
            setCurrentItem(firstItem);
            addRecentlyViewed(firstItem.id, firstItem);
            addToHistory(firstItem.id);
            setTotalFound(response.total_count);
        } catch (err) {
            const message = extractErrorMessage(err, "Search failed");
            setError(message);
            toast.error(message);
        } finally {
            setIsSearching(false);
        }
    }, [
        searchQuery,
        filters.tag_query,
        filters.media_type,
        filters.min_views,
        filters.max_views,
        filters.min_votes,
        filters.max_votes,
        filters.has_watermark,
        filters.min_ai_confidence,
        setIsSearching,
        setError,
        setSearchResults,
        setCurrentItem,
        addRecentlyViewed,
        addToHistory,
        setTotalFound,
    ]);

    const handleFetchExactCount = useCallback(async () => {
        setIsLoadingExactCount(true);
        try {
            const response = await splClient.getMediaCount({
                tagQuery: filters?.tag_query,
                tags: filters?.tags,
                tagMode: filters?.tag_mode,
                minViews: filters?.min_views,
                maxViews: filters?.max_views,
                minVotes: filters?.min_votes,
                maxVotes: filters?.max_votes,
                searchText: filters?.search_text,
                mediaType: filters?.media_type === "all"
                    ? undefined
                    : filters?.media_type,
                hasWatermark: filters?.has_watermark,
                minAiConfidence: filters?.min_ai_confidence,
            });
            setExactCount(response.count);
        } catch {
            toast.error("Failed to fetch exact count");
        } finally {
            setIsLoadingExactCount(false);
        }
    }, [filters, setExactCount, setIsLoadingExactCount]);

    const handleTagStatsClick = useCallback(
        async (tagOrQuery: string) => {
            let newTagQuery: string;
            if (
                tagOrQuery.includes(" AND ") || tagOrQuery.includes(" OR ") ||
                tagOrQuery.includes("NOT ")
            ) {
                newTagQuery = tagOrQuery;
            } else {
                const currentQuery = filters.tag_query?.trim() || "";
                newTagQuery = currentQuery
                    ? `${currentQuery} AND ${tagOrQuery}`
                    : tagOrQuery;
            }

            setActiveLeftTab("browse");
            setFilters({
                tag_query: newTagQuery,
            });
            clearSearch();
            resetOrderedState();

            setIsLoading(true);
            setError(null);
            try {
                const newFilters = {
                    ...filters,
                    tag_query: newTagQuery,
                };
                if (browsingMode === "random") {
                    const response = await splClient.getNextImage({
                        id: undefined,
                        filters: newFilters,
                        exclude_ids: [],
                    });
                    setCurrentItem(response);
                    setTotalFound(response.total_found);
                    addRecentlyViewed(response.id, response);
                    addToHistory(response.id);
                } else {
                    const response = await fetchOrderedMedia(
                        browsingMode,
                        newFilters,
                        0,
                        50,
                    );
                    if (response.items.length > 0) {
                        setOrderedItems(
                            response.items,
                            response.total_count,
                            response.has_more,
                        );
                        setTotalFound(response.total_count);
                        setCurrentItem(response.items[0]);
                        addRecentlyViewed(
                            response.items[0].id,
                            response.items[0],
                        );
                        addToHistory(response.items[0].id);
                    } else {
                        setError("No media found");
                    }
                }
            } catch (err) {
                const message = extractErrorMessage(
                    err,
                    "Failed to load media",
                );
                setError(message);
                toast.error(message);
            } finally {
                setIsLoading(false);
            }
        },
        [
            filters,
            browsingMode,
            setFilters,
            clearSearch,
            resetOrderedState,
            setIsLoading,
            setError,
            setCurrentItem,
            setTotalFound,
            addRecentlyViewed,
            addToHistory,
            setOrderedItems,
            setActiveLeftTab,
        ],
    );

    const handleClearSearchAndBrowse = useCallback(() => {
        clearSearch();
        loadNextItem();
    }, [clearSearch, loadNextItem]);

    return {
        handleApplyFilters,
        handleBrowsingModeChange,
        handleSearch,
        handleFetchExactCount,
        handleTagStatsClick,
        handleClearSearchAndBrowse,
    };
};
